import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  EstimationCostResponse,
  UpsertEstimationCostRequest,
} from '@/api/schema/data-contracts';
import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type { MutationProps } from '../types';

export const useEstimationCostQueryActions = () => {
  const { auth } = useAuth();
  const {
    estimationCostController: { v1EstimationsCostsList, v1EstimationsCostsUpdate },
  } = useApi();

  const queryClient = useQueryClient();

  const getEstimationCostsKey = 'estimation-costs';

  const useGetEstimationCosts = (estimationId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && Boolean(estimationId),
      queryKey: [getEstimationCostsKey, estimationId],
      queryFn: async () => {
        const { data } = await v1EstimationsCostsList(estimationId!);
        return data as EstimationCostResponse | '' /* for 204 */;
      },
    });

  const useUpdateEstimationCosts = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['update-estimation-costs'],
      mutationFn: async ({
        estimationId,
        updateEstimationCostsData,
      }: {
        estimationId: string;
        updateEstimationCostsData: UpsertEstimationCostRequest;
      }) => {
        const { data } = await v1EstimationsCostsUpdate(estimationId, updateEstimationCostsData);
        return data;
      },
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          queryKey: [getEstimationCostsKey, variables.estimationId],
        });
      },
    });

  return { useGetEstimationCosts, useUpdateEstimationCosts };
};
